import Icons from 'components/atoms/Icons';
import React, { useEffect, useState } from 'react';
import Sidebar from '../shared/Sidebar';
import SliderTabs from '../shared/SliderTabs';
import Modal from 'assets/shared/Modal';
import axios from 'axios';
import constant from 'assets/utils/constant';
import LocationApptQuestions from './LocationApptQuestions';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';

type Appt = {
	Time: string;
	DisplayTime: string;
	ArrivalTime: string;
};

type LocationActionWidgetProps = {
	mammogram: string;
	pharmacy: boolean;
	urgent: boolean;
	prompt: boolean;
	deptId: string;
	providerId: string;
	locationName: string;
	street1: string;
	suite: string;
	city: string;
	state: string;
	zip: string;
	phone: string;
};

const LocationActionWidget = (props: LocationActionWidgetProps): JSX.Element => {
	const {
		mammogram,
		pharmacy,
		urgent,
		prompt,
		deptId,
		providerId,
		locationName,
		street1,
		suite,
		city,
		state,
		zip,
		phone,
	} = props;
	const [mobileShown, setMobileShown] = useState(false);
	const [waitTime, setWaitTime] = useState('');
	const [nextAppt, setNextAppt] = useState('');
	const [size, setSize] = useState('sm');

	const updateWaitTime = (waitTimeSeconds: number) => {
		const waitTimeElement = document.getElementById('walk-in-wait-time');
		if (waitTimeElement) {
			const waitTimeMinutes = Math.floor(waitTimeSeconds / 60);
			const waitTimeHours = Math.floor(waitTimeMinutes / 60);
			const remainingMinutes = waitTimeMinutes % 60;

			let formattedWaitTime;
			if (waitTimeHours > 0) {
				formattedWaitTime = `${waitTimeHours} Hour${
					waitTimeHours > 1 ? 's' : ''
				} and ${remainingMinutes} Minute${remainingMinutes > 1 ? 's' : ''}`;
			} else {
				formattedWaitTime = `${waitTimeMinutes} Minute${waitTimeMinutes > 1 ? 's' : ''}`;
			}

			setWaitTime(formattedWaitTime);
		}
	};

	const getWaitTimes = async () => {
		try {
			const response = await axios.post(
				constant.api.epicApi + constant.api.getDepartmentWaitTimes,
				{
					Departments: [
						{
							ID: deptId,
							Type: 'External',
						},
					],
					MinutesToLookBack_: 60,
					UseAppointmentTimeIfAfterStartEvent_: true,
					CalculateForOverallLevel_: false,
					CalculateForEachDepartment_: false,
					StartEvent: { ID: '', Type: '' },
					EndEvent: { ID: '', Type: '' },
				},
				{
					headers: {
						'Content-Type': 'application/json',
						'Epic-Client-ID': constant.api.epicClientID,
						Accept: 'application/json',
						Authorization: `Basic ${constant.api.epicAccessToken}`,
					},
				}
			);
			const waitTimeSeconds = response.data.AverageWaitTime;
			updateWaitTime(waitTimeSeconds);
		} catch (error) {
			console.error('Error fetching wait times:', error);
		}
	};

	(urgent || prompt) && deptId && getWaitTimes();

	const getNextAppointment = async () => {
		const today = new Date().toISOString().split('T')[0];
		const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

		try {
			const response = await axios.post(
				constant.api.epicApi + constant.api.getScheduleDays,
				{
					StartDate: today,
					EndDate: tomorrow,
					ProviderID: providerId,
					ProviderIDType: 'External',
					VisitTypeIDs: [{ ID: '24692', Type: 'External' }],
				},
				{
					headers: {
						'Content-Type': 'application/json',
						'Epic-Client-ID': constant.api.epicClientID,
						Accept: 'application/json',
						Authorization: `Basic ${constant.api.epicAccessToken}`,
					},
				}
			);

			const scheduleDays = response.data.ScheduleDays;
			let nextAppointment = null;
			let nextAppointmentDate = null;

			for (const day of scheduleDays) {
				if (day.Slots.length > 0) {
					nextAppointment = day.Slots[0];
					nextAppointmentDate = day.Date;
					break;
				}
			}
			if (nextAppointment) {
				updateNextAppointment(nextAppointment, nextAppointmentDate);
			} else {
				updateNextAppointment(null);
			}
		} catch (error) {
			console.error('Error fetching next appointment:', error);
		}
	};

	const updateNextAppointment = (slot?: Appt | null, date?: string | null) => {
		const nextAppointmentElement = document.getElementById('next-appt-time');

		if (nextAppointmentElement) {
			if (slot) {
				const appointmentTime = new Date(`${date}T${slot.Time}`);
				const hours = appointmentTime.getHours();
				const minutes = appointmentTime.getMinutes();
				const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
				const formattedHours = hours % 12 || 12;
				const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
				const today = new Date().toISOString().split('T')[0];

				const formattedDate = date === today ? 'Today' : 'Tomorrow';
				setNextAppt(`${formattedDate} at ${formattedHours}:${formattedMinutes} ${ampm}`);
			} else {
				setNextAppt('There is no availability for new appointments today or tomorrow.');
			}
		}
	};

	urgent && providerId && getNextAppointment();

	useEffect(() => {
		const resize = () => {
			const viewportSize = getOsfViewportSize();
			setSize(viewportSize);
		};
		resize();
		window.addEventListener('resize', resize);
		return () => window.removeEventListener('resize', resize);
	});

	return (
		<>
			{(mammogram || pharmacy || urgent || prompt) && (
				<Sidebar
					header={
						mammogram
							? 'Schedule a Mammogram'
							: pharmacy
							? 'Refill Prescription'
							: urgent
							? 'Get Care'
							: 'Get In Line'
					}
					pageType={'location'}
					customHeader="osf-inner-h2"
				>
					<div className="location-action-widget">
						{mammogram && (
							<>
								<div className="profile-box-outer schedule-sidebar">
									<div className="profile-action-box-container schedule">
										<div className="profile-action-box">
											<div className="mobile-drawer">
												<div className={`drawer-contents ${mobileShown ? 'hidden' : ''}`}>
													<div className="details-section">
														<SliderTabs tabsText={['New Patient', 'Returning Patient']}>
															<>
																<p className="osf-nav-section-titles">{locationName}</p>
																<div className="address">
																	<Icons id="location" />
																	<p>
																		<span className="street-address">
																			{street1}
																			{suite}{' '}
																		</span>
																		<span className="city-state">
																			{city}, {state} {zip}
																		</span>
																	</p>
																</div>
																<button
																	className="btn-primary btn-fw mobile drawer-btn"
																	onClick={() => setMobileShown(true)}
																>
																	Schedule Online
																</button>
																{(size == 'lg' || size == 'xl') && (
																	<Modal
																		customClass="modal action-modal"
																		openBtnClass="btn-primary btn-fw desktop"
																		openBtnLabel="Schedule Online"
																		portalId="mammogram-modal-root"
																	>
																		<div className="modal-details">
																			<h2>{locationName}</h2>
																			<p className="subtitle">New Patient Appointment</p>
																		</div>
																		<div className="mychart-container">
																			<LocationApptQuestions deptId={deptId} phone={phone} />
																		</div>
																	</Modal>
																)}
															</>
															<>
																<p className="log-in">Log in to OSF MyChart to Schedule</p>
																<a
																	href="https://osfmychart.org/OSFMyChart/Authentication/Login?postloginurl=scheduling"
																	target="_blank"
																	className="mychart-btn btn-primary btn-fw no-icon"
																>
																	OSF MyChart
																</a>
															</>
														</SliderTabs>
													</div>
												</div>
												{(size == 'xs' || size == 'sm' || size == 'md') && (
													<div className={`mychart-container ${mobileShown ? 'shown' : ''}`}>
														<LocationApptQuestions deptId={deptId} phone={phone} />
														<button
															className="back-btn btn-secondary btn-fw no-icon"
															onClick={() => setMobileShown(false)}
														>
															Back
														</button>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>
								<div id="mammogram-modal-root"></div>
							</>
						)}
						{pharmacy && (
							<div className="profile-box-outer schedule-sidebar">
								<div
									className="profile-action-box-container pharmacy"
									data-module="MobileDrawerFactory"
								>
									<div className="profile-action-box">
										<div className="mobile-drawer">
											<div className="drawer-contents">
												<div className="inner-content shown">
													<p>Website may take a few minutes to load.</p>
													<p>Thank you for your patience!</p>
													<a
														href="https://www.cornerdrugstore.com/ExpressRefill"
														target="_blank"
														className="btn-primary btn-fw no-icon"
													>
														Request a Refill
													</a>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						)}
						{urgent && (
							<>
								<div
									className="profile-box-outer schedule-sidebar"
									data-epic-dept-id={deptId}
									data-epic-provider-id={providerId}
								>
									<div className="profile-action-box-container urgent-care">
										<div className="profile-action-box">
											<div className="mobile-drawer">
												<div className={`drawer-contents ${mobileShown ? 'hidden' : ''}`}>
													<SliderTabs tabsText={['In-person Care', 'Virtual Care']}>
														<>
															<div className="in-person">
																<div className="inner-content in-person-care">
																	<p>
																		<strong>Walk-in or schedule an appointment:</strong>
																	</p>
																	<ul>
																		<li>
																			<Icons id="person" />
																			<span>Walk-in wait time: </span>
																			<span id="walk-in-wait-time" className="walk-in-wait-time">
																				{waitTime}
																			</span>
																		</li>
																		<li>
																			<Icons id="pld-calendar" />
																			<span>Next appointment:</span>
																			<span id="next-appt-time" className="walk-in-wait-time">
																				{nextAppt}
																			</span>
																		</li>
																	</ul>
																	<button
																		className="btn-primary btn-fw mobile"
																		onClick={() => setMobileShown(true)}
																	>
																		Schedule an Appointment or Get in Line
																	</button>
																</div>
															</div>
															<Modal
																customClass="modal action-modal"
																openBtnClass="btn-primary btn-fw desktop"
																openBtnLabel="Schedule an Appointment or Get in Line"
																portalId="modal-urgent-root"
															>
																<div className="modal-details">
																	<h2>{locationName}</h2>
																	<p className="subtitle">New Patient Appointment</p>
																</div>
																<div className="mychart-container">
																	<iframe
																		src={`https://www.osfmychart.org/OSFMyChart/Scheduling/OnMyWay/widget?willSkipMap=1&selDepId=${deptId}&linksource=directory`}
																		frameBorder="0"
																		allowFullScreen
																		tabIndex={0}
																	></iframe>
																</div>
															</Modal>
														</>
														<>
															<div className="virtual-care">
																<div className="inner-content">
																	<p>
																		Connect to care 24/7 whenever and wherever you need it. No
																		appointment needed.
																	</p>
																	<a
																		href="https://www.osfhealthcare.org/c/oncall-virtual-visit/"
																		className="btn-primary btn-fw no-icon"
																		target="_blank"
																	>
																		Virtual Visit Options
																	</a>
																</div>
															</div>
														</>
													</SliderTabs>
												</div>
												<div className={`mychart-container ${mobileShown ? 'shown' : ''}`}>
													<iframe
														src={`https://www.osfmychart.org/OSFMyChart/Scheduling/OnMyWay/widget?willSkipMap=1&selDepId=${deptId}&linksource=directory`}
														frameBorder="0"
														allowFullScreen
														tabIndex={0}
													></iframe>
													<button
														className="back-btn btn-secondary btn-fw no-icon"
														onClick={() => setMobileShown(false)}
													>
														Back
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div id="modal-urgent-root"></div>
							</>
						)}
						{prompt && (
							<>
								<div className="profile-box-outer schedule-sidebar">
									<div
										className="profile-action-box-container prompt-care"
										data-epic-dept-id={deptId}
									>
										<div className="profile-action-box">
											<div className="mobile-drawer">
												<div className={`drawer-contents ${mobileShown ? 'hidden' : ''}`}>
													<div className="inner-content shown">
														<p>
															<Icons id="person" />
															<span>Walk-in wait time: </span>
															<span id="walk-in-wait-time" className="walk-in-wait-time">
																&nbsp;{waitTime}
															</span>
														</p>
														<p>
															By getting in line, the clinic will be notified of your expected
															arrival time and will save your place in line.
														</p>
														<button
															className="btn-primary drawer-btn btn-fw mobile"
															onClick={() => setMobileShown(true)}
														>
															Get in Line
														</button>
													</div>
												</div>
												<div className={`mychart-container ${mobileShown ? 'shown' : ''}`}>
													<iframe
														src={`https://www.osfmychart.org/OSFMyChart/Scheduling/OnMyWay/widget?willSkipMap=1&selRfvId=15&selDepId=${deptId}&linksource=directory`}
														frameBorder="0"
														allowFullScreen
														tabIndex={0}
													></iframe>
													<button
														className="back-btn btn-secondary btn-fw no-icon"
														onClick={() => setMobileShown(false)}
													>
														Back
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
								<Modal
									customClass="modal action-modal"
									openBtnClass="btn-primary btn-fw desktop"
									openBtnLabel="Get in Line"
									portalId="modal-prompt-root"
								>
									<div className="modal-details">
										<h2>{locationName}</h2>
										<p className="subtitle">New Patient Appointment</p>
									</div>
									<div className="mychart-container">
										<iframe
											src={`https://www.osfmychart.org/OSFMyChart/Scheduling/OnMyWay/widget?willSkipMap=1&selRfvId=15&selDepId=${deptId}&linksource=directory`}
											frameBorder="0"
											allowFullScreen
											tabIndex={0}
										></iframe>
									</div>
								</Modal>
								<div id="modal-prompt-root" />
							</>
						)}
					</div>
				</Sidebar>
			)}
		</>
	);
};

export default LocationActionWidget;
